import React, { useState } from "react";
import dayjs from "dayjs";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import ToastMessage from "../../ToastMessage";
import useScreenWidth from "../../../store/useScreenWidth";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import useTicketNotification from "../../../store/ReactQueries/ticketNotification.ts/useTicketNotif";
import useUpdateTicketNotif from "../../../store/ReactQueries/ticketNotification.ts/useUpdateTicketNotif";
import useUpdateAllTicketNotif from "../../../store/ReactQueries/ticketNotification.ts/useUpdateAllTicketNotif";
import { useNavigate } from "react-router-dom";
import { EndPoints } from "../../../utils/apiEndpoints";
import { darkModeBackgroud } from "../../../utils/constants";
import { useExpandNavMenu } from "../../../store/useExpandNavMenu";
import { useLanguageStore } from "../../../store/useLanguageStore";
import { useDarkModeStore } from "../../../store/useDarkModeStore";
import { NotificationInterface, NotificationType } from "../../../models/notification";

const NotificationBtn = () => {

  const navigate = useNavigate();
  const isSmallScreen = useScreenWidth();
  const { isCollapsed } = useExpandNavMenu();
  const { language } = useLanguageStore();
  const { darkMode } = useDarkModeStore();
  const { data: notifications } = useTicketNotification();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const updateNotifications = useUpdateTicketNotif();
  const updateAllNotifications = useUpdateAllTicketNotif();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);

  const handleClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);

  const handlePosition = () => {
    if (isSmallScreen) return "-70px";
    else return isCollapsed ? "-280px" : "-130px";
  };

  const checkForNotificationType = (objectId: string, notificationType: NotificationType): void => {
    switch (notificationType) {
      case "Ticket":
        navigate(`${EndPoints.TICKETS}/view/${objectId}`);
        break;
      case "TicketFeedback":
        navigate(`${EndPoints.TICKETS}/view/${objectId}/true`);
        break;
      case "PurchaseOrder":
        navigate(`${EndPoints.PURCHASEORDERS}/view/${objectId}`);
        break;
      case "PurchaseOrderApproval":
        navigate(`${EndPoints.PENDINGREQUESTS}`);
        break;
      default:
        console.error("Unknown notification type");
    }
  };

  const handleReadNotification = async (
    notification: NotificationInterface
  ): Promise<void> => {
    updateNotifications.mutate(notification, {
      onSuccess: () => {
        handleClose();
        checkForNotificationType(notification.objectId, notification.notificationType);
      },
      onError: (error) => ToastMessage.Failed(error.message),
    });
  };

  const handleReadAllNotif = () => {
    if (!notifications || notifications.length === 0) {
      ToastMessage.Failed("No notifications to mark as read.");
      return;
    }
    updateAllNotifications.mutate(notifications, {
      onSuccess: () => handleClose(),
      onError: (error) => ToastMessage.Failed(error.message),
    });
  };

  return (
    <React.Fragment>
      <Button
        className="button-hover-circle"
        variant="text"
        onClick={handleClick}
        title={language['Notifications']}
      >
        <div className="icon-sidebar-top-color position-relative">
          {
            <NotificationsOutlinedIcon
              className="icon-sidebar-top-color"
              sx={{
                width: "22px",
                height: "22px",
                color: darkMode ? "white" : "#4D5F71",
              }}
            />
          }
          {(notifications?.length ?? 0) > 0
            && <span
              className="position-absolute"
              style={{
                top: "-9px",
                right: "0px",
                fontSize: "19px",
                color: "#FF3E1D",
              }}
            >
              &#9679;
            </span>
          }
        </div>
      </Button>
      <Popover
        id="notification"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={{
          "& .MuiPopover-paper": {
            marginLeft: handlePosition(),
          },
        }}
      >
        <div
          className="d-flex flex-column"
          style={{
            width: isSmallScreen ? "250px" : "400px",
            color: darkMode ? "white" : "#32475C99",
            background: darkMode ? darkModeBackgroud : "white",
            paddingBottom: "1px",
          }}
        >
          <div
            className="p-3 d-flex justify-content-between align-items-center"
            style={{
              height: "50px",
              borderBottom: `1px solid ${darkMode ? 'white' : 'rgba(50, 71, 92, 0.12)'}`,
            }}
          >
            <span
              style={{
                fontSize: "16px",
                fontWeight: 600,
                color: darkMode ? 'white' : "#32475CDE",
                opacity: 0.8,
              }}
            >
              {language["Notification"]}
            </span>
            <Chip
              label={`${language["NEW"]} ${notifications?.length}`}
              sx={{
                borderRadius: "3px",
                fontSize: "12px",
                color: darkMode ? 'white' : 'rgb(105,108,255)',
                fontWeight: 600,
                background: "rgba(106,108,255, 0.16)",
                opacity: 0.9,
                cursor: "default",
              }}
            />
          </div>
          <div
            className="d-flex flex-column"
            style={{ maxHeight: "354px", overflowY: "auto" }}
          >
            {notifications && notifications.length > 0
              ? notifications?.map((notif) => (
                <div
                  key={notif.id}
                  title={notif.message}
                  className={`notification-item-bar ${darkMode && 'notification-item-bar-dark-mode'}`}
                  role="button"
                  onClick={() => handleReadNotification(notif)}
                  style={isSmallScreen ? { padding: "12px 5px" } : {}}
                >
                  <div
                    className="d-flex flex-column gap-1 ms-3"
                    style={{ width: isSmallScreen ? 100 : 200 }}
                  >
                    <span
                      className="text-truncate"
                      style={{
                        fontSize: "14px",
                        color: darkMode ? "white" : "rgba(50, 71, 92, 0.87)",
                        maxWidth: isSmallScreen ? 100 : 200,
                      }}
                    >
                      {notif.message}
                    </span>
                  </div>
                  <span
                    className="ms-auto text-nowrap"
                    style={{
                      fontSize: "12px",
                      color: darkMode ? "white" : "32475C61",
                      maxWidth: 100,
                    }}
                  >
                    {dayjs(notif.dateInserted).format("DD MMM YYYY H:mm")}
                  </span>
                </div>
              ))
              : <div
                className="p-2 m-2 text-center alert alert-pink"
                style={{ fontSize: 13 }}
              >
                {language["NoDataAvailable"]}
              </div>
            }
          </div>
          {
            notifications
            && notifications.length > 0
            && <div
              className="p-3 ps-4 pe-4 d-flex align-items-center justify-content-center"
              style={{ borderTop: "1px solid rgba(50, 71, 92, 0.12)" }}
            >
              <Button
                variant="contained"
                onClick={handleReadAllNotif}
                className="w-75"
                sx={{ background: "#696CFF", fontSize: 12 }}
              >
                {language["READALLNOTIFICATIONS"]}
              </Button>
            </div>
          }
        </div>
      </Popover>
    </React.Fragment>
  );
};

export default NotificationBtn;
